/**
 * 構造化データ（JSON-LD）のルート別定義。
 *
 * router/index.js の beforeEach から setJsonLd(to.name) で参照され、
 * 各ページの <head> に <script type="application/ld+json"> として注入される。
 *
 * 元データ: 「構造化データ コピペ用」HTML（2026-06-21 作成）
 * 検証: https://search.google.com/test/rich-results / https://validator.schema.org/
 */

// 全ページ共通の発行元情報
const organization = {
    '@type': 'Organization',
    '@id': 'https://adtasukaru.com/#organization',
    name: '株式会社グレイトヘルプ',
    url: 'https://adtasukaru.com/',
    logo: {
        '@type': 'ImageObject',
        url: 'https://adtasukaru.com/img/lp/logo.png',
    },
    description: 'ウェブマーケティング・媒体制作・SaaS開発を行うIT企業、株式会社グレイトヘルプ。広告成果連携ツール「アドタスカル」を開発・提供しています。',
    telephone: '058-214-9005',
    email: 'inquiry@adtasukaru.com',
    address: {
        '@type': 'PostalAddress',
        postalCode: '500-8468',
        addressCountry: 'JP',
        addressRegion: '岐阜県',
        addressLocality: '岐阜市',
        streetAddress: '加納桜田町2-5-2',
    },
    sameAs: ['https://twitter.com/adtasukaru'],
};

// トップページのみ、Organization に対応ツール一覧（OfferCatalog）を付与する
const organizationWithOfferCatalog = {
    ...organization,
    hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: '対応広告媒体・ツール一覧',
        itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Google広告成果連携ツール「アドタスカル」', url: 'https://adtasukaru.com/google-cv' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Yahoo!検索広告成果連携ツール「アドタスカル」', url: 'https://adtasukaru.com/yahoo-cv' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'YDA成果連携ツール「アドタスカル」', url: 'https://adtasukaru.com/yda-cv' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Microsoft広告成果連携ツール「アドタスカル」', url: 'https://adtasukaru.com/microsoft-cv' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'TikTok広告成果連携ツール「アドタスカル」', url: 'https://adtasukaru.com/tiktok-cv' } },
        ],
    },
};

// WebSite（トップページのみ）
const website = {
    '@type': 'WebSite',
    '@id': 'https://adtasukaru.com/#website',
    url: 'https://adtasukaru.com/',
    name: 'アドタスカル',
    alternateName: '広告成果連携ツール「アドタスカル」',
    description: '広告成果連携ツール「アドタスカル」。Google広告・Yahoo!広告・Microsoft広告・Meta広告・TikTok広告などの広告管理画面とASP管理画面の成果（コンバージョン）の乖離を、オフラインコンバージョン／コンバージョンAPI連携で自動・半自動で解消します。連携ASP90社超。CVタグの設置に悩まず、正確なCVデータで広告の自動最適化がかかります。',
    inLanguage: 'ja',
    publisher: { '@id': 'https://adtasukaru.com/#organization' },
};

/**
 * 各CVツールページの JSON-LD（Organization + Service）を生成する
 * @param slug ルートのパス（例: google-cv）
 * @param name サービス名
 * @param description サービス説明
 * @returns {object}
 */
const createServiceGraph = (slug, name, description) => {
    const service = {
        '@type': 'Service',
        '@id': `https://adtasukaru.com/${slug}#service`,
        name,
        description,
        serviceType: '広告成果連携（オフラインコンバージョン／コンバージョンAPI連携）',
        provider: { '@id': 'https://adtasukaru.com/#organization' },
        areaServed: '日本',
        url: `https://adtasukaru.com/${slug}`,
    };

    const result = {
        '@context': 'https://schema.org',
        '@graph': [organization, service],
    };
    return result;
};

// Organization のみのページ（@graph に Organization だけ）
const organizationOnlyGraph = {
    '@context': 'https://schema.org',
    '@graph': [organization],
};

// ルート名（router の name）→ JSON-LD
export const jsonLdByRouteName = {
    home: {
        '@context': 'https://schema.org',
        '@graph': [organizationWithOfferCatalog, website],
    },
    'google-cv': createServiceGraph(
        'google-cv',
        'Google広告成果連携ツール「アドタスカル」',
        'Google広告成果連携ツール「アドタスカル」は、ASP管理画面に計上される成果とGoogle広告(検索・ディスプレイ)管理画面上の成果の乖離をなくすツールです。オフラインコンバージョン機能を用いて、本来は手動で行う作業を自動化・半自動化でき、その手間を効率化。CVが正確に計上されるので広告の自動最適化がかかり、ASPへのタグ設置の依頼も不要です。'
    ),
    'yahoo-cv': createServiceGraph(
        'yahoo-cv',
        'Yahoo!検索広告成果連携ツール「アドタスカル」',
        'Yahoo!検索広告成果連携ツール「アドタスカル」は、ASP管理画面に計上される成果とYahoo!検索広告(検索)管理画面上の成果の乖離をなくすツールです。オフラインコンバージョン機能を用いて、本来は手動で行う作業を自動化・半自動化でき、その手間を効率化。CVが正確に計上されるので広告の自動最適化がかかり、ASPへのタグ設置の依頼も不要です。'
    ),
    'yda-cv': createServiceGraph(
        'yda-cv',
        'YDA成果連携ツール「アドタスカル」',
        'YDA成果連携ツール「アドタスカル」は、コンバージョンAPI機能によって、ASP管理画面に計上される成果とYDA(Yahoo!ディスプレイ広告)管理画面上の成果の乖離をなくすツールです。自動・半自動でコンバージョンの乖離をなくし、正確なCVデータで自動最適化がかかります。ASPへのタグ設置の依頼も不要です。'
    ),
    'microsoft-cv': createServiceGraph(
        'microsoft-cv',
        'Microsoft広告成果連携ツール「アドタスカル」',
        'Microsoft広告成果連携ツール「アドタスカル」は、ASP管理画面に計上される成果とMicrosoft広告管理画面上の成果の乖離をなくすツールです。オフラインコンバージョン機能を用いて、本来は手動で行う作業を自動化・半自動化でき、その手間を効率化。CVが正確に計上されるので広告の自動最適化がかかり、ASPへのタグ設置の依頼も不要です。'
    ),
    'tiktok-cv': createServiceGraph(
        'tiktok-cv',
        'TikTok広告成果連携ツール「アドタスカル」',
        'TikTok広告成果連携ツール「アドタスカル」は、コンバージョンAPI機能によって、ASP管理画面に計上される成果とTikTok管理画面上の成果の乖離をなくすツールです。自動・半自動でコンバージョンの乖離をなくし、正確なCVデータで自動最適化がかかります。ASPへのタグ設置の依頼も不要です。'
    ),
    'contact-form': organizationOnlyGraph,
    kiyaku: organizationOnlyGraph,
    tokuteisho: organizationOnlyGraph,
    'privacy-policy': organizationOnlyGraph,
};
